import { Module } from 'vuex';
import { IRootState } from './store';
import restService from '@/services/restService';

export enum NoticePriority
{
  Info,
  Warning,
  Danger,
  Sticky
}

export enum NoticeStatus
{
  Draft,
  Active,
  Deleted
}

export interface INotice {
  id: string;
  from: string;
  title: string;
  text: string;
  externalLink: string | null | undefined;
  validFrom: string | null | undefined;
  validUntil: string | null | undefined;
  priority: NoticePriority;
  status: NoticeStatus;

  /**List of matching passenger Ids */
  passengers: number[];
}

export interface INoticeUserState {
  noticeId: string;
  userId: string;
  readAt: string;
}

export interface INoticeState {
  notices: INotice[];
  userStates: INoticeUserState[];
}


// STATE
const messageState: INoticeState = {
  notices: [],
  userStates: [],
};

// GETTERS
const getters = {
  noticeById: (state: INoticeState) => (id: string) => {
    return state.notices.find(notice => notice.id === id);
  },
  noticeUserStateByNoticeId: (state: INoticeState) => (noticeId: string) => {
    return state.userStates.find(state => state.noticeId === noticeId) ?? null;
  },
  passengerNotices: (state: INoticeState) => (passengerMovitId: number | null | undefined) => {
    if (passengerMovitId === null || passengerMovitId === undefined) {
      return [];
    }
    return state.notices.filter(notice => notice.passengers.includes(passengerMovitId));
  },
};

// MUTATIONS
const mutations = {
  setNotices: (state: INoticeState, notices: INotice[]) => {
    state.notices = notices;
  },
  clearUserData: (state: INoticeState) => {
    state.notices = [];
    state.userStates = [];
  },
  setNoticeUserStates: (state: INoticeState, states: INoticeUserState[]) => {
    state.userStates = states;
  },
  setNoticeAsRead: (state: INoticeState, readNotice: INoticeUserState) => {
    const stateIndex = state.userStates.findIndex(s => s.noticeId === readNotice.noticeId && s.userId === readNotice.userId);
    if (stateIndex > -1) {
      state.userStates.splice(stateIndex, 1, readNotice);
    }
    else {
      state.userStates.push(readNotice);
    }
  }

};

// ACTIONS
const actions = {
  initializeNotices: async ({commit, dispatch}: {commit: any, dispatch: any}) => {
    await dispatch('getNotices');
    await dispatch('getNoticeUserStates');
  },
  getNotices: async ({commit}: {commit: any}) => {
    try {
      const notices = await restService.notices.getNotices();
      commit('setNotices', notices);
    }
    catch (error) {
      commit('app/error', (error as any).message || error, { root: true });
    }
  },
  getNoticeUserStates: async ({commit}: {commit: any}) => {
    try {
      const states = await restService.notices.getNoticeUserStates();
      commit('setNoticeUserStates', states);
    }
    catch (error) {
      commit('app/error', (error as any).message || error, { root: true });
    }
  }, 
  markNoticeAsRead: async ({commit}: {commit: any}, noticeId: string) => {
    try {
      const readNotice = await restService.notices.markNoticeAsRead(noticeId);
      commit('setNoticeAsRead', readNotice);
    }
    catch (error) {
      commit('app/error', (error as any).message || error, { root: true });
    }
  }

};

const appStore: Module<INoticeState, IRootState> = {
  namespaced: true,
  state: messageState,
  mutations,
  actions,
  getters,
};
export default appStore;
